import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { cn } from "@/lib/utils.ts";
import {
  createContext,
  type CSSProperties,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DEFAULT_PANEL_MIN_WIDTH,
  type PanelSide,
  PanelResizeHandle,
  useResizablePanel,
} from "./useResizablePanel.tsx";

/**
 * Slack in the narrowed comparison. Both sides come from a measured container,
 * and a column resting exactly at its ceiling measures 379.98 as often as 380.
 */
const NARROWED_TOLERANCE_PX = 1;

type SplitPanelColumn = {
  /**
   * The left column is narrower than the width it rests at.
   *
   * Answered from the committed width rather than from the column's own box.
   * The split already knows both numbers, and content that measures itself gets
   * a different answer: it reads the gutters as narrowness, and its observer
   * reports a frame after the state that moved the divider — late enough that a
   * request to widen the column arrives after whatever read this.
   *
   * Only a drag can make it true. Above `@3xl` the resting default resolves to
   * `max(--panel-w-default, 100cqw - restingMaxLeft)`, whose second term always
   * wins by the time the split exists, so a column with nothing committed rests
   * at exactly `restingMaxLeft`. Below `@3xl` there is one column and no
   * divider. A cancelled drag never commits, so it never counts.
   */
  isLeftNarrowed: boolean;
  /** Return the split to its resting width, as double-clicking the divider does. */
  restoreLeftWidth: () => void;
};

const SplitPanelColumnContext = createContext<SplitPanelColumn | null>(null);

/**
 * What the split above this content can tell it about its own column, or `null`
 * where there is no split — mobile renders a single column and no provider, and
 * "nothing narrowed it" is the honest answer there rather than a default.
 */
export const useSplitPanelColumn = () => useContext(SplitPanelColumnContext);

export type SplitPanelLayoutProps = {
  /** Left column. */
  left: ReactNode;
  /** Right column. */
  right: ReactNode;
  /** Whether the left column is shown. Controlled — the screen owns the state. */
  leftOpen?: boolean;
  /** Whether the right column is shown. Controlled — the screen owns the state. */
  rightOpen?: boolean;
  /** localStorage key for the committed divider width (per device). */
  storageKey: string;
  /**
   * Which column's width the stored number is. Defaults to the right, which is
   * the column this layout sizes; pass `"left"` for a split whose left column is
   * the one with a width of its own and whose right is simply the rest of the
   * screen. See {@link useResizablePanel}'s `persistedSide`.
   */
  persistedSide?: PanelSide;
  /** Min width the left column keeps at any divider position. */
  minLeft?: number;
  /** Min width the right column keeps at any divider position. */
  minRight?: number;
  /**
   * Max width the right column may be dragged to. Omitted, it may take
   * everything the left column's floor doesn't need — right for a column being
   * read in, wrong for one holding a single card, which past a certain width
   * stops reading as a panel and starts reading as a modal.
   */
  maxRight?: number;
  /**
   * Width the left column keeps against a drag past {@link maxRight}. With it,
   * `maxRight` is where the right column may always be dragged to rather than
   * its cap: past it the column keeps widening while the left stays this wide.
   * A second floor for the left, above {@link minLeft}, that only applies once
   * the right column has what the cap used to allow — so a wide display gives
   * its spare width to the right column and a narrow one loses nothing.
   */
  keepLeft?: number;
  /**
   * Max width the left column may reach by dragging. The left column flexes to
   * fill, so its ceiling is enforced by flooring the right column at
   * `container − maxLeft`. Omitted, the left column keeps absorbing whatever the
   * viewport gives it.
   */
  maxLeft?: number;
  /**
   * Max width the left column may reach *before any drag* — the resting default,
   * which {@link defaultWidthClassName} is floored against.
   *
   * Separate from {@link maxLeft} because the two answer different questions: how
   * wide the user may make the column, versus how wide it is when they haven't
   * asked. Collapsing them means widening the drag ceiling also widens the
   * resting default on large displays, since the default is derived from it —
   * a column that may be dragged wider would then *start* wider on a large
   * monitor, which is not the same choice.
   *
   * Defaults to `maxLeft`, so callers that want one number keep the old
   * behavior unchanged.
   */
  restingMaxLeft?: number;
  /**
   * Sets `--panel-w-default` at `@3xl`+ — the right column's width when no px
   * width is committed, so a fresh split sizes sensibly until the user drags.
   * Without it the right column is full-width at every breakpoint until dragged.
   * Any CSS length: a fraction where the column's content scales with the
   * container (`@3xl:[--panel-w-default:min(480px,40cqw)]`), a fixed floor where
   * {@link restingMaxLeft} should decide the split instead — the two combine as
   * `max(this, 100cqw - restingMaxLeft)`.
   *
   * Never size it in `%`. The value is read both as the right grid track, where
   * `%` resolves against the container, and as `--reserve` in the left content
   * pin below, where it resolves against the left track — so a `%` here pins the
   * left content wider than the column that clips it, and anything right-aligned
   * in that panel's header lands past the divider, invisible. Use `cqw`, which
   * means the container to both readers.
   */
  defaultWidthClassName?: string;
  /**
   * Pins the left column's content to the width it has while open
   * (`100cqw − --reserve`), so a *collapsing* left column clips behind the
   * moving divider instead of rewrapping every frame.
   *
   * `100cqw` is the nearest container query container, which for a split filling
   * a screen's content area is the split itself. A split nested inside another's
   * column is not: there `100cqw` is still the outer container, so the pin comes
   * out wider than the column that clips it — content centered inside it sits
   * off-center, and its right edge is cut off. Pass false there. Nothing is lost
   * unless the left column closes, which is the only thing the pin is for; the
   * content then just follows its track.
   */
  pinLeftWidth?: boolean;
  /** Extra classes on the left column. */
  leftClassName?: string;
  /** Extra classes on the right column. */
  rightClassName?: string;
  className?: string;
};

/**
 * Generic two-column side-by-side split with a draggable divider. Both columns
 * are collapsible; the screen controls each with `leftOpen`/`rightOpen` (the
 * calendar closes the left threads column, the thread screen closes the right
 * canvas column). When only one column is open it fills the width and the divider
 * hides; when both are open the divider splits them, clamped so neither drops
 * below its min width.
 *
 * The **right** column carries the committed drag width (the left flexes to fill).
 * A shared `storageKey` therefore pins one divider position across surfaces — the
 * continuity the canvas relies on. Which column's width that stored number *is*
 * follows {@link SplitPanelLayoutProps.persistedSide}: the right by default, the
 * left where the left is the column with a width of its own.
 *
 * Layout is a CSS grid whose columns are `minmax(0, 1fr)` (left, fills the
 * remainder) and `var(--right-track)` (right). Open/close is a plain
 * `grid-template-columns` transition — the browser animates the track sizes, so
 * there's no measured-width slide to run in JS. `--right-track` resolves to:
 * `0` (right closed), `100%` (right is the only open column), or the committed /
 * resting width at `@3xl` when both are open. The left content is pinned to
 * its open width (`100cqw − --reserve`) so a collapsing column clips behind the
 * moving divider instead of rewrapping every frame — unless `pinLeftWidth` says
 * otherwise, which a nested split has to.
 *
 * Desktop (≥`@3xl`): both open columns sit side-by-side, split by the divider.
 * Below `@3xl`: one column at a time (the screen keeps a single column open).
 * A closed column collapses to zero width rather than unmounting, so
 * scroll/focus/spotlight state survives a close→open cycle (content is lazily
 * mounted on first open — see `hasOpened`).
 */
export const SplitPanelLayout = ({
  left,
  right,
  leftOpen = true,
  rightOpen = true,
  storageKey,
  persistedSide,
  minLeft = DEFAULT_PANEL_MIN_WIDTH,
  minRight = DEFAULT_PANEL_MIN_WIDTH,
  maxLeft,
  maxRight,
  keepLeft,
  restingMaxLeft = maxLeft,
  defaultWidthClassName,
  pinLeftWidth = true,
  leftClassName,
  rightClassName,
  className,
}: SplitPanelLayoutProps) => {
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    widthCss,
    committedFlexWidth,
    isResizing,
    startResize,
    nudgeWidth,
    resetWidth,
    min,
    max,
  } = useResizablePanel({
    side: "right",
    persistedSide,
    storageKey,
    min: minRight,
    max: maxRight,
    keepOther: keepLeft,
    minOther: minLeft,
    maxOther: maxLeft,
    containerRef,
  });

  // The right column takes the committed px width only when both columns are
  // open; if the left is closed the right flexes to fill instead.
  const bothOpen = leftOpen && rightOpen;

  // Hoisted out of the memo deliberately. Memoizing on the numbers behind it
  // would hand every consumer a new object on each measurement, while the answer
  // is a boolean that does not move as one is dragged past.
  const isLeftNarrowed =
    restingMaxLeft != null &&
    committedFlexWidth != null &&
    committedFlexWidth < restingMaxLeft - NARROWED_TOLERANCE_PX;

  const column = useMemo(
    () => ({ isLeftNarrowed, restoreLeftWidth: resetWidth }),
    [isLeftNarrowed, resetWidth],
  );

  // Mount each column on first open and keep it mounted thereafter, so a
  // close/open is a pure width animation — unmounting the moment it closes makes
  // the exit chunky. A column never opened (e.g. a screen that keeps it closed)
  // stays unmounted, so it costs nothing until first opened.
  const [leftMounted, setLeftMounted] = useState(leftOpen);
  const [rightMounted, setRightMounted] = useState(rightOpen);
  useEffect(() => {
    if (leftOpen) setLeftMounted(true);
  }, [leftOpen]);
  useEffect(() => {
    if (rightOpen) setRightMounted(true);
  }, [rightOpen]);

  // Content that measures its container (e.g. FullCalendar's ResizeObserver) can
  // fire mid-transition and miss the final settled width. Ping one window resize
  // just after the open/close transition (200ms) completes so embedded content
  // re-measures against the settled layout.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — re-fire on every open/close to nudge embedded content post-transition
  useEffect(() => {
    const t = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 220);
    return () => clearTimeout(t);
  }, [leftOpen, rightOpen]);

  const transition =
    !isResizing &&
    !isMobile &&
    "transition-[grid-template-columns] duration-200 ease-out motion-reduce:transition-none";

  const fade = (open: boolean) =>
    cn(
      "flex min-h-0 flex-1 flex-col",
      !isMobile && "transition-opacity duration-100 ease-out",
      open ? "opacity-100" : "opacity-0",
    );

  const leftPanel = (
    <div
      className={cn("relative flex min-h-0 flex-col overflow-hidden", leftOpen && leftClassName)}
      inert={!leftOpen}
    >
      {leftMounted ? (
        <div
          className={fade(leftOpen)}
          // Pin the content to its open width (container minus the right
          // column) so a collapsing column clips it behind the moving divider
          // instead of rewrapping every frame. `--reserve` mirrors the right
          // column's width whenever the right is open, and drops to 0 when the
          // right is closed so the left content fills. The explicit width is the
          // cross-axis size; `flex-1` still stretches the content to full height.
          // Left unset by a nested split, where `100cqw` measures the wrong box
          // — see `pinLeftWidth`.
          style={pinLeftWidth ? { width: "calc(100cqw - var(--reserve))" } : undefined}
        >
          {left}
        </div>
      ) : null}
    </div>
  );

  const rightPanel = (
    <div
      className={cn("relative isolate flex min-h-0 flex-col overflow-hidden", rightClassName)}
      inert={!rightOpen}
    >
      {rightMounted ? <div className={fade(rightOpen)}>{right}</div> : null}
      {/* `min === max` whenever the container cannot afford both columns' floors:
          `useResizablePanel` settles that collision by letting the floor win, which
          leaves a handle whose every drag clamps to one number. Rendering it anyway
          offers a control that cannot move and tells a screen reader, through
          `aria-valuemin === aria-valuemax`, that it is adjustable. */}
      {bothOpen && min < max && (
        <PanelResizeHandle
          side="right"
          onResizeStart={startResize}
          onNudgeWidth={nudgeWidth}
          onResetWidth={resetWidth}
          isResizing={isResizing}
          min={min}
          max={max}
        />
      )}
    </div>
  );

  return (
    <SplitPanelColumnContext.Provider value={column}>
      <div
        ref={containerRef}
        className={cn(
          "relative grid min-h-0 flex-1 overflow-hidden grid-rows-[minmax(0,1fr)]",
          // Left fills the remainder; right takes `--right-track`. A
          // grid-template-columns transition animates the open/close.
          "grid-cols-[minmax(0,1fr)_var(--right-track)]",
          transition,
          // Base tracks (all widths): the right column fills when it's the only
          // open column and collapses to 0 when closed. The `@3xl` overrides below
          // apply the committed/resting split when both columns are open.
          rightOpen ? "[--right-track:100%]" : "[--right-track:0px]",
          "[--reserve:0px]",
          // Sets `--panel-w-default` at `@3xl`+ (the right column's pre-drag width).
          defaultWidthClassName,
          bothOpen && "@3xl:[--right-track:var(--panel-track)]",
          // The left content reserves the right column's width whenever the right
          // is open — including while the left collapses — so it never reflows.
          rightOpen && "@3xl:[--reserve:var(--panel-track)]",
          className,
        )}
        style={
          {
            // The right column's intended width when both are open: the committed
            // px width once dragged, else the host's pre-drag default. When
            // `restingMaxLeft` is set, that default is floored at
            // `container − restingMaxLeft` so the flex (left) column honors its
            // resting ceiling before any drag — otherwise the default sizes the
            // right column freely and a fresh split on a wide viewport hands the
            // left column more than it should start with. This floors against the
            // *resting* ceiling, not the drag ceiling: the drag ceiling is where
            // the user may take the column, not where it opens.
            //
            // A host that wants the left column to rest at exactly `restingMaxLeft`
            // passes only its own minimum here, leaving the `100cqw −` term to win
            // wherever the container can afford both.
            "--panel-track":
              widthCss != null
                ? "var(--panel-w)"
                : restingMaxLeft != null
                  ? `max(var(--panel-w-default, 100%), calc(100cqw - ${restingMaxLeft}px))`
                  : "var(--panel-w-default, 100%)",
            ...(widthCss != null ? { "--panel-w": widthCss } : {}),
          } as CSSProperties
        }
      >
        {leftPanel}
        {rightPanel}
      </div>
    </SplitPanelColumnContext.Provider>
  );
};
